import React from "react";

type SharedFieldProps = {
  label?: string;
  error?: string;
  helperText?: string;
  requiredMark?: boolean;
  containerClassName?: string;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  placeholder?: string;
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

export type YhInputProps = SharedFieldProps &
  Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    keyof SharedFieldProps | "size"
  > & {
    /** Render as multiline textarea */
    multiline?: boolean;
    /** Textarea rows (default 4 when multiline) */
    rows?: number;
    /** Alias — same as multiline */
    textarea?: boolean;
  };

export const YhInput = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  YhInputProps
>(
  (
    {
      label,
      error,
      helperText,
      requiredMark,
      containerClassName = "",
      className = "",
      endAdornment,
      startAdornment,
      id,
      multiline,
      textarea,
      rows = 4,
      ...rest
    },
    ref
  ) => {
    const inputId = id || rest.name;
    const isTextarea = Boolean(multiline || textarea);
    const { type: inputType, ...fieldRest } = rest as YhInputProps & {
      type?: string;
    };

    const fieldClass = `yh-input ${error ? "yh-input-error" : ""} ${
      startAdornment ? "pl-10" : ""
    } ${endAdornment ? "pr-10" : ""} ${
      isTextarea ? "min-h-[96px] resize-y leading-relaxed" : ""
    } ${className}`;

    return (
      <div className={`w-full ${containerClassName}`}>
        {label && (
          <label htmlFor={inputId} className="yh-label">
            {requiredMark && <span className="mr-0.5 text-yh-red">*</span>}
            {label}
          </label>
        )}
        <div className={`relative flex ${isTextarea ? "items-start" : "items-center"}`}>
          {startAdornment && (
            <div
              className={`absolute left-3 z-[1] flex items-center text-yh-light-gray ${
                isTextarea ? "top-3" : ""
              }`}
            >
              {startAdornment}
            </div>
          )}
          {isTextarea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              id={inputId}
              rows={rows}
              className={fieldClass}
              {...(fieldRest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              id={inputId}
              type={inputType}
              className={fieldClass}
              {...(fieldRest as React.InputHTMLAttributes<HTMLInputElement>)}
            />
          )}
          {endAdornment && (
            <div
              className={`absolute right-3 z-[1] flex items-center text-yh-light-gray ${
                isTextarea ? "top-3" : ""
              }`}
            >
              {endAdornment}
            </div>
          )}
        </div>
        {(error || helperText) && (
          <p
            className={`mt-1 text-xs ${
              error ? "text-yh-red" : "text-yh-light-gray"
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

YhInput.displayName = "YhInput";

export default YhInput;
